import React, { useEffect, useRef, useState } from 'react'
import { Modal, ModalHeader } from 'reactstrap'
import { useSelector } from 'react-redux'
import AddDataboxModal, { AddDataboxModalHandle } from '../Teams/modals/AddDataboxModal'
import AddDataProductsModal, {
  AddDataProductsModalHandle
} from '../Teams/modals/AddDataProductsModal'
import PersonalDataboxTab, { PersonalDataboxTabHandle } from './PersonalDataboxTab'
import { IconFolderRoot, IconX } from '@tabler/icons-react'

export type DataSourceTab = 'personalDatabox' | 'databox' | 'dataProducts'

interface DataSourcesModalProps {
  isOpen: boolean
  toggle: () => void
  initialTab?: DataSourceTab
  // Databox tab
  existingDataboxes?: any[]
  smartInsightsTeamIdentifier?: string
  addDataboxToAgent?: (databoxes: any) => void
  removeDataboxFromAgent?: (databoxIds: string[]) => void
  // When set, opens the Databoxes tab directly into editing that specific databox's files/data
  // products
  initialDataboxId?: string | null
  // Data products tab
  existingDataProducts?: any[]
  addDataProductToAgent?: (dataProducts: any) => void
  removeDataProductFromAgent?: (productIds: string[]) => void
  // True while the agent's existing data sources are still being fetched from the server
  isLoadingExistingSources?: boolean
  // Forwarded to the Databoxes tab - see AddDataboxModal's useAgentWizardTeamContext doc. Default
  // true preserves existing Agent Wizard behavior; the chat "Add sources" flow passes false.
  useAgentWizardTeamContext?: boolean
  // Forwarded to the Databoxes tab
  isTeamAgent?: boolean
  agentScopeId?: string
  // Forwarded to the Databoxes tab - see AddDataboxModal's useAgentWizardTeamContext doc
  isEnterpriseAgent?: boolean
  lockedDataboxIds?: string[]
  lockedDataProductIds?: string[]
  lockedItemIds?: string[]
  lockedPersonalFileIds?: string[]
}

// Single shared modal for picking data sources - "Databoxes" and "Data Products" tabs each load
// the existing AddDataboxModal/AddDataProductsModal content (via their asTabContent prop) so
// there's one modal shell instead of two separate modals.
const DataSourcesModal: React.FC<DataSourcesModalProps> = ({
  isOpen,
  toggle,
  initialTab = 'personalDatabox',
  existingDataboxes = [],
  smartInsightsTeamIdentifier,
  addDataboxToAgent,
  removeDataboxFromAgent,
  initialDataboxId = null,
  existingDataProducts = [],
  addDataProductToAgent,
  removeDataProductFromAgent,
  isLoadingExistingSources = false,
  useAgentWizardTeamContext = true,
  isTeamAgent = false,
  agentScopeId,
  isEnterpriseAgent = false,
  lockedDataboxIds = [],
  lockedDataProductIds = [],
  lockedItemIds = [],
  lockedPersonalFileIds = []
}) => {
  // A Team agent has no Personal Databox of its own, so that tab is never applicable
  const agent = useSelector((state: any) => state?.agentReducer?.agent)
  const isTeamTypeAgent = useAgentWizardTeamContext ? agent?.agentType === 'Team' : isTeamAgent
  // An Enterprise agent only ever deals with the Community team's databoxes - no Personal Databox
  const isEnterpriseTypeAgent = useAgentWizardTeamContext
    ? agent?.agentType === 'Enterprise'
    : isEnterpriseAgent

  // Personal Databox can never be the initial tab for a Team/Enterprise agent - fall back to Team Databoxes.
  const resolveInitialTab = (): DataSourceTab => {
    if (initialDataboxId) return 'databox'
    if ((isTeamTypeAgent || isEnterpriseTypeAgent) && initialTab === 'personalDatabox') {
      return 'databox'
    }
    return initialTab
  }

  const [activeTab, setActiveTab] = useState<DataSourceTab>(resolveInitialTab())

  // Live (not-yet-saved) selection reported by each tab, null until a tab reports at least once.
  const [liveDataboxSelection, setLiveDataboxSelection] = useState<any[] | null>(null)
  const [liveDataProductSelection, setLiveDataProductSelection] = useState<any[] | null>(null)
  const [livePersonalSelection, setLivePersonalSelection] = useState<any[] | null>(null)

  // Whether the user has any teams / any accessible data products / an actual Personal Databox
  // at all - defaults to true so a tab isn't hidden before its availability check resolves
  const [hasTeams, setHasTeams] = useState(true)
  const [hasDataProducts, setHasDataProducts] = useState(true)
  const [hasPersonalDatabox, setHasPersonalDatabox] = useState(true)

  // Whether each tab's OWN async data is still loading - defaults to true so nothing renders as
  // "complete" before a tab has actually reported in.
  const [isPersonalDataboxLoading, setIsPersonalDataboxLoading] = useState(true)
  const [isTeamDataboxLoading, setIsTeamDataboxLoading] = useState(true)
  const [isDataProductsLoading, setIsDataProductsLoading] = useState(true)

  // Imperative handles so removing a databox/data product from the OTHER tab's panel can
  // directly uncheck it in its owning tab too
  const databoxModalRef = useRef<AddDataboxModalHandle>(null)
  const dataProductsModalRef = useRef<AddDataProductsModalHandle>(null)
  const personalModalRef = useRef<PersonalDataboxTabHandle>(null)

  // `existingDataboxes` mixes Team Databoxes tab entries with the (at most one) Personal
  // Databox tab entry, tagged `scope: 'Personal'`
  const teamOnlyExistingDataboxes = existingDataboxes.filter((db: any) => db.scope !== 'Personal')
  const personalOnlyExistingDataboxes = existingDataboxes.filter(
    (db: any) => db.scope === 'Personal'
  )

  // Reset to whichever tab was requested each time the modal is (re)opened
  useEffect(() => {
    if (isOpen) setActiveTab(resolveInitialTab())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialTab, initialDataboxId, isTeamTypeAgent, isEnterpriseTypeAgent])

  // Clear stale live selections once the modal closes so a future open starts fresh
  useEffect(() => {
    if (!isOpen) {
      setLiveDataboxSelection(null)
      setLiveDataProductSelection(null)
      setLivePersonalSelection(null)
      setHasTeams(true)
      setHasDataProducts(true)
      setHasPersonalDatabox(true)
      setIsPersonalDataboxLoading(true)
      setIsTeamDataboxLoading(true)
      setIsDataProductsLoading(true)
    }
  }, [isOpen])

  // Per-category loading, shared across tabs; Team/Enterprise agents have no Personal Databox tab.
  const isFetchingPersonal =
    isLoadingExistingSources ||
    (!isTeamTypeAgent && !isEnterpriseTypeAgent && isPersonalDataboxLoading)
  const isFetchingTeam = isLoadingExistingSources || isTeamDataboxLoading
  const isFetchingDataProducts = isLoadingExistingSources || isDataProductsLoading

  // If the active tab's section turns out to be unavailable (and thus gets hidden), fall back to
  // the first still-available tab, in priority order: Personal Databox, Team Databoxes, Data
  // Products. E.g. if Personal Databox isn't available, this auto-selects Team Databoxes.
  useEffect(() => {
    const availableTabs: DataSourceTab[] = [
      ...(hasPersonalDatabox && !isTeamTypeAgent && !isEnterpriseTypeAgent
        ? (['personalDatabox'] as const)
        : []),
      ...(hasTeams ? (['databox'] as const) : []),
      ...(hasDataProducts ? (['dataProducts'] as const) : [])
    ]
    if (!availableTabs.includes(activeTab) && availableTabs.length > 0) {
      setActiveTab(availableTabs[0])
    }
  }, [
    activeTab,
    hasTeams,
    hasDataProducts,
    hasPersonalDatabox,
    isTeamTypeAgent,
    isEnterpriseTypeAgent
  ])

  // Rendered by whichever tab is active, as the first item of its own left column (instead of a
  // standalone row above everything), so the right-hand Selected Sources panel/divider - a
  // sibling of that same left column - starts level with the tabs instead of below them with a
  // gap of empty space up top.
  const tabsBar = !initialDataboxId && (
    <div className="flex theme-surface flex-shrink-0 mb-1">
      {(
        [
          {
            key: 'personalDatabox',
            label: 'My Personal Databox',
            visible: hasPersonalDatabox && !isTeamTypeAgent && !isEnterpriseTypeAgent
          },
          {
            key: 'databox',
            label: isEnterpriseTypeAgent ? 'Databoxes' : 'Team Databoxes',
            visible: hasTeams
          },
          { key: 'dataProducts', label: 'Data Products', visible: hasDataProducts }
        ] as const
      )
        .filter((tab) => tab.visible)
        .map((tab) => (
          <div
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-2 pb-50 text-center cursor-pointer -mb-px ${
              activeTab === tab.key
                ? 'border-b-[5px] border-[var(--color-primary)] text-[var(--color-primary)] font-medium'
                : 'theme-text-subheading'
            }`}>
            {tab.label}
          </div>
        ))}
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="xl"
      contentClassName="w-[70vw] max-w-[2000px] h-[90vh] max-h-[1000px] mx-auto flex flex-col">
      <ModalHeader
        className={`!justify-between !border-0 !flex !items-center chat-primary theme-bg flex-shrink-0`}
        toggle={toggle}
        close={
          <IconX
            size={20}
            onClick={toggle}
            className="text-white transition-opacity duration-300 ease-in-out hover:opacity-80 cursor-pointer"
            data-testid="close-button"
          />
        }>
        <div className="flex items-center gap-[8px]">
          <IconFolderRoot size={20} className="text-white" />
          <span className="text-white">Add sources</span>
        </div>
      </ModalHeader>
      {!initialDataboxId && (
        <div className={activeTab === 'personalDatabox' ? 'contents' : 'hidden'}>
          <PersonalDataboxTab
            ref={personalModalRef}
            isOpen={isOpen}
            toggle={toggle}
            tabsBar={tabsBar}
            existingDataboxes={existingDataboxes}
            lockedDataboxIds={lockedDataboxIds}
            lockedFileIds={lockedItemIds}
            lockedPersonalFileIds={lockedPersonalFileIds}
            lockedDataProductIds={lockedDataProductIds}
            addDataboxToAgent={addDataboxToAgent}
            removeDataboxFromAgent={removeDataboxFromAgent}
            existingTeamDataboxesForPanel={liveDataboxSelection ?? teamOnlyExistingDataboxes}
            onRemoveTeamDatabox={(databoxId) => {
              databoxModalRef.current?.uncheckDatabox(databoxId)
            }}
            onRemoveTeamDataboxItem={(databoxId, itemId) => {
              databoxModalRef.current?.removeItem(databoxId, itemId)
            }}
            existingDataProductsForPanel={liveDataProductSelection ?? existingDataProducts}
            onRemoveDataProduct={(id) => {
              dataProductsModalRef.current?.uncheckDataProduct(id)
            }}
            hasExistingDataProducts={existingDataProducts.length > 0}
            onLiveSelectionChange={setLivePersonalSelection}
            onPersonalDataboxAvailabilityChange={setHasPersonalDatabox}
            onLoadingChange={setIsPersonalDataboxLoading}
            isFetchingTeam={isFetchingTeam}
            isFetchingDataProducts={isFetchingDataProducts}
            isLoadingExistingSources={isLoadingExistingSources}
            onBeforeSubmit={() => dataProductsModalRef.current?.commitPendingSelection()}
          />
        </div>
      )}
      {/* Both tabs stay mounted the whole time (just hidden via CSS) instead of being
      conditionally rendered, so switching tabs doesn't lose an in-progress, not-yet-saved
      selection made in the other tab. */}
      <div className={activeTab === 'databox' ? 'contents' : 'hidden'}>
        <AddDataboxModal
          ref={databoxModalRef}
          asTabContent
          isOpen={isOpen}
          toggle={toggle}
          tabsBar={tabsBar}
          existingDataboxes={existingDataboxes}
          lockedDataboxIds={lockedDataboxIds}
          lockedItemIds={lockedItemIds}
          lockedDataProductIds={lockedDataProductIds}
          smartInsightsTeamIdentifier={smartInsightsTeamIdentifier}
          addDataboxToAgent={addDataboxToAgent}
          removeDataboxFromAgent={removeDataboxFromAgent}
          initialDataboxId={initialDataboxId}
          standaloneDataProducts={liveDataProductSelection ?? existingDataProducts}
          onRemoveDataProduct={(id) => {
            dataProductsModalRef.current?.uncheckDataProduct(id)
          }}
          hasExistingDataProducts={existingDataProducts.length > 0}
          existingPersonalDataboxForPanel={livePersonalSelection ?? personalOnlyExistingDataboxes}
          onRemovePersonalDatabox={() => personalModalRef.current?.clearSelection()}
          onRemovePersonalFile={(fileId) => personalModalRef.current?.removeFile(fileId)}
          onLiveSelectionChange={setLiveDataboxSelection}
          onBeforeSubmit={() => dataProductsModalRef.current?.commitPendingSelection()}
          onTeamsAvailabilityChange={setHasTeams}
          onLoadingChange={setIsTeamDataboxLoading}
          isFetchingPersonal={isFetchingPersonal}
          isFetchingDataProducts={isFetchingDataProducts}
          isLoadingExistingSources={isLoadingExistingSources}
          useAgentWizardTeamContext={useAgentWizardTeamContext}
          isTeamAgent={isTeamAgent}
          agentScopeId={agentScopeId}
          isEnterpriseAgent={isEnterpriseAgent}
        />
      </div>
      {!initialDataboxId && (
        <div className={activeTab === 'dataProducts' ? 'contents' : 'hidden'}>
          <AddDataProductsModal
            ref={dataProductsModalRef}
            asTabContent
            isOpen={isOpen}
            toggle={toggle}
            tabsBar={tabsBar}
            existingDataProducts={existingDataProducts}
            addDataProductToAgent={addDataProductToAgent}
            removeDataProductFromAgent={removeDataProductFromAgent}
            lockedDataProductIds={lockedDataProductIds}
            lockedDataboxIds={lockedDataboxIds}
            lockedItemIds={lockedItemIds}
            existingDataboxesForPanel={liveDataboxSelection ?? teamOnlyExistingDataboxes}
            onRemoveDatabox={(databoxId) => {
              const personalId = (livePersonalSelection ?? personalOnlyExistingDataboxes)[0]?.id
              if (personalId && String(databoxId) === String(personalId)) {
                personalModalRef.current?.clearSelection()
                return
              }
              databoxModalRef.current?.uncheckDatabox(databoxId)
            }}
            onRemoveDataboxItem={(databoxId, itemId) => {
              const personalId = (livePersonalSelection ?? personalOnlyExistingDataboxes)[0]?.id
              if (personalId && String(databoxId) === String(personalId)) {
                personalModalRef.current?.removeFile(itemId)
                return
              }
              databoxModalRef.current?.removeItem(databoxId, itemId)
            }}
            existingPersonalDataboxForPanel={livePersonalSelection ?? personalOnlyExistingDataboxes}
            hasExistingDataboxes={existingDataboxes.length > 0}
            onLiveSelectionChange={setLiveDataProductSelection}
            onBeforeSubmit={() => databoxModalRef.current?.commitPendingSelection()}
            onDataProductsAvailabilityChange={setHasDataProducts}
            onLoadingChange={setIsDataProductsLoading}
            isFetchingPersonal={isFetchingPersonal}
            isFetchingTeam={isFetchingTeam}
            isLoadingExistingSources={isLoadingExistingSources}
            useAgentWizardTeamContext={useAgentWizardTeamContext}
            isEnterpriseAgent={isEnterpriseAgent}
          />
        </div>
      )}
    </Modal>
  )
}

export default DataSourcesModal
